import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiUsers, FiCalendar, FiBook, FiBarChart2, FiFileText, FiAlertCircle, FiCreditCard } from 'react-icons/fi';
import ChildSelector from '../../components/parent/ChildSelector';
import { useParentChild } from '../../contexts/ParentChildContext';
import api from '../../services/api';

const QUICK_LINKS = [
  { to: '/parent/evaluations', icon: FiBook, label: 'Évaluations', color: 'text-blue-600 bg-blue-50' },
  { to: '/parent/notes', icon: FiBarChart2, label: 'Résultats', color: 'text-green-600 bg-green-50' },
  { to: '/parent/documents', icon: FiFileText, label: 'Documents', color: 'text-purple-600 bg-purple-50' },
  { to: '/parent/absences', icon: FiAlertCircle, label: 'Absences', color: 'text-amber-600 bg-amber-50' },
  { to: '/parent/fees', icon: FiCreditCard, label: 'Scolarité', color: 'text-emerald-600 bg-emerald-50' },
  { to: '/parent/appointments', icon: FiCalendar, label: 'Rendez-vous', color: 'text-primary-600 bg-primary-50' },
];

const ParentDashboard = () => {
  const { children, loading: childLoading } = useParentChild();
  const [stats, setStats] = useState({ upcoming: 0, overdue: 0, absences: 0 });

  const inscriptionId = children[0]?.inscription_id;

  useEffect(() => {
    if (!inscriptionId) return;
    Promise.all([
      api.get(`/parent-portal/children/${inscriptionId}/evaluations`, { params: { upcoming: '1' } }),
      api.get(`/parent-portal/children/${inscriptionId}/fees`),
      api.get(`/parent-portal/children/${inscriptionId}/absences`),
    ])
      .then(([ev, fees, abs]) => {
        setStats({
          upcoming: ev.data.success ? ev.data.data.length : 0,
          overdue: fees.data.success ? fees.data.summary?.overdue_amount || 0 : 0,
          absences: abs.data.success ? abs.data.data.length : 0,
        });
      })
      .catch(() => {});
  }, [inscriptionId]);

  if (childLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ChildSelector />

      <div>
        <h2 className="text-xl font-bold text-slate-900">Tableau de bord</h2>
        <p className="text-sm text-slate-500">Suivi scolaire de vos enfants</p>
      </div>

      {children.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.upcoming}</p>
            <p className="text-xs text-slate-500 mt-1">Éval. à venir</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.absences}</p>
            <p className="text-xs text-slate-500 mt-1">Absences</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-red-600">
              {stats.overdue > 0 ? new Intl.NumberFormat('fr-FR').format(stats.overdue) : '0'}
            </p>
            <p className="text-xs text-slate-500 mt-1">Impayés (FCFA)</p>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {QUICK_LINKS.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-primary-200 hover:shadow-sm transition-all"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color}`}>
                <Icon size={18} />
              </div>
              <span className="font-medium text-slate-800">{item.label}</span>
            </Link>
          );
        })}
      </div>

      {children.length > 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <FiUsers /> Mes enfants
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {children.map((child) => (
              <div key={child.eleve_id} className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{child.prenom} {child.nom}</p>
                <p className="text-sm text-slate-500">{child.classe} — {child.annee_scolaire}</p>
                <p className="text-xs text-slate-400">Matricule : {child.matricule}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentDashboard;
